/**
 * Data layer for the order blotter (ARCHITECTURE.md §7, §8, §4;
 * ARCHITECTURE-LAYERS.md §4.1): `hc<AppType>` fetchers and `queryOptions`
 * factories. The mutation hook lives in `./hooks`.
 */
import { infiniteQueryOptions } from "@tanstack/react-query";
import type { BadgeTone } from "@richrich/ui";
import type { ListOrdersResponse, Order, OrderStatus, OrderStatusGroup } from "@richrich/contracts";
import { api, unwrap } from "../../lib/api";
import { keys } from "../../lib/queryKeys";

/** UI filter: the two Toss status groups plus an unfiltered view. */
export type OrderFilter = "all" | OrderStatusGroup;

/** Keyset-paginated order list, honoring the server's `nextCursor`. */
export function ordersInfiniteQuery(filter: OrderFilter) {
  return infiniteQueryOptions({
    queryKey: keys.orders.list(filter),
    queryFn: async ({ pageParam }): Promise<ListOrdersResponse> => {
      const query: Record<string, string> = { limit: "50" };
      if (filter !== "all") query.status = filter;
      if (typeof pageParam === "string") query.cursor = pageParam;
      return unwrap<ListOrdersResponse>(await api.orders.$get({ query }));
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 10_000,
  });
}

/** `POST /api/orders/:id/cancel` — internal `mutationFn` for {@link useCancelOrder} (`./hooks`). */
export async function cancelOrder(id: string): Promise<Order> {
  return unwrap<Order>(await api.orders[":id"].cancel.$post({ param: { id } }));
}

/** Toss statuses that the operator may cancel (mirrors the server set). */
const CANCELABLE: ReadonlySet<OrderStatus> = new Set<OrderStatus>([
  "intent",
  "PENDING",
  "PARTIAL_FILLED",
  "PENDING_REPLACE",
]);

/** Whether a manual cancel is offered for this order. */
export function isCancelable(order: Order): boolean {
  return order.closedAt === null && CANCELABLE.has(order.status);
}

/** Badge tone + label for an order's lifecycle status. */
export function orderStatusBadge(status: OrderStatus): { tone: BadgeTone; label: string } {
  switch (status) {
    case "FILLED":
      return { tone: "good", label: "Filled" };
    case "PARTIAL_FILLED":
      return { tone: "info", label: "Partial" };
    case "PENDING":
      return { tone: "info", label: "Pending" };
    case "intent":
      return { tone: "neutral", label: "Intent" };
    case "PENDING_CANCEL":
      return { tone: "warning", label: "Cancelling" };
    case "PENDING_REPLACE":
      return { tone: "warning", label: "Replacing" };
    case "CANCELED":
      return { tone: "neutral", label: "Canceled" };
    case "REPLACED":
      return { tone: "neutral", label: "Replaced" };
    case "REJECTED":
      return { tone: "critical", label: "Rejected" };
    case "CANCEL_REJECTED":
      return { tone: "critical", label: "Cancel rejected" };
    case "REPLACE_REJECTED":
      return { tone: "critical", label: "Replace rejected" };
    default:
      return { tone: "neutral", label: status };
  }
}
